import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./CreditCardExpense.css";
import CreditCardDepositTable from "./CreditCardDepositTable";
import ConfirmSubmitModal from "./ConfirmSubmitModal";
import PaymentPercentInput from "./PaymentPercentInput";
import BankAccountFilter from "./BankAccountFilter";

const CreditCardExpense = ({ initialFilterStartDate, initialFilterEndDate }) => {
    const [expenseTransactions, setExpenseTransactions] = useState([]);
    const [grantList, setGrantList] = useState([]);
    const [showEditDefaultPaymentPopup, setShowEditDefaultPaymentPopup] =
        useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showConfirmedRecords, setShowConfirmedRecords] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const fileInputRef = useRef(null);
    const [customizedCategories, setCustomizedCategories] = useState([]);
    const [categoriesByExpenseType, setCategoriesByExpenseType] = useState({});
    const [expenseTypes, setExpenseTypes] = useState([]);
    const fixedCategories = [
        "Materials", "Overhead", "Travel", "Fee", "Equipment",
        "Sub-contract", "Internal Bank Transfer", "Credit Card Payment",
        "N/A (Non-grant Item)",
    ];
    const categories = [...new Set([...fixedCategories, ...expenseTypes])];
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [expensePeriod, setExpensePeriod] = useState("all");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [showCustomPeriodPopup, setShowCustomPeriodPopup] = useState(false);
    const [depositTransactions, setDepositTransactions] = useState([]);
    const [ripplingPayrollTransactions, setRipplingPayrollTransactions] =
        useState([]);
    const [selectedBank, setSelectedBank] = useState("pnc");
    const [showEditNotePopup, setShowEditNotePopup] = useState(false);
    const [transactionToEditNote, setTransactionToEditNote] = useState(null);
    const [availableBankAccounts, setAvailableBankAccounts] = useState([]);
    const [selectedBankAccounts, setSelectedBankAccounts] = useState([]);
    const [showBankColumns, setShowBankColumns] = useState(false);

    // Itemization state
    const [expandedItemRows, setExpandedItemRows] = useState({});

    const toggleItemization = (transactionId) => {
        setExpandedItemRows(prev => ({ ...prev, [transactionId]: !prev[transactionId] }));
    };

    const getLineItems = (transaction) => {
        try { return JSON.parse(transaction["Line Items"] || "[]"); } catch { return []; }
    };

    const addLineItem = async (transaction) => {
        const items = getLineItems(transaction);
        items.push({ id: Date.now().toString(), name: '', description: '', amount: '', grantCategory: '', customizedCategory: 'N/A', payment: {} });
        await saveLineItems(transaction, items);
    };

    const updateLineItem = async (transaction, itemId, field, value) => {
        const items = getLineItems(transaction);
        const idx = items.findIndex(i => i.id === itemId);
        if (idx === -1) return;
        items[idx][field] = value;
        await saveLineItems(transaction, items);
    };

    const removeLineItem = async (transaction, itemId) => {
        await saveLineItems(transaction, getLineItems(transaction).filter(i => i.id !== itemId));
    };

    const saveLineItems = async (transaction, items) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/${transaction.ID}`,
                { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
                  body: JSON.stringify({ ...transaction, "Line Items": JSON.stringify(items) }) }
            );
            if (response.ok) {
                setExpenseTransactions(prev => prev.map(t => t.ID === transaction.ID ? { ...t, "Line Items": JSON.stringify(items) } : t));
            }
        } catch (error) { console.error("Error saving line items:", error); }
    };

    useEffect(() => {
        if (initialFilterStartDate && initialFilterEndDate) {
          setCustomStartDate(initialFilterStartDate);
          setCustomEndDate(initialFilterEndDate);
          setExpensePeriod('custom');
          // No need to show popup since we're setting dates programmatically
          setShowCustomPeriodPopup(false);
        }
    }, [initialFilterStartDate, initialFilterEndDate]);

    const handleEditNoteClick = (transaction) => {
        setTransactionToEditNote(transaction);
        setShowEditNotePopup(true);
    };

    const handleSaveNote = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/note/${transactionToEditNote.ID}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({ Note: transactionToEditNote.Note }),
                }
            );

            if (response.ok) {
                // Refresh transaction data
                await fetchExpenseTransactions();
                setShowEditNotePopup(false);
            } else {
                throw new Error("Failed to update note");
            }
        } catch (error) {
            console.error("Error updating note:", error);
            alert("Error updating note.");
        }
    };

    const bankOptions = [
        { value: "pnc", label: "PNC Bank" },
        { value: "boa", label: "Bank of America" },
    ];

    // Fetch bank accounts from the company endpoint (using unified /company/banks)
    const fetchBankAccounts = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/company/banks`,
                {
                    credentials: "include"
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            const banks = data.banks || [];
            const accounts = [];
            
            // Convert flat banks array to account list
            banks.forEach((bank, index) => {
                const bankName = bank.bankName;
                const accountType = bank.accountType || 'Checking';
                const accountNumber = bank.accountNumber || '';
                const lastFour = accountNumber.slice(-4) || accountNumber;
                
                // Only include credit accounts for CreditCardExpense
                if (accountType.toLowerCase() === 'credit') {
                    accounts.push({
                        bank: bankName,
                        accountNumber: `****${lastFour}`,
                        accountType: accountType,
                        // Lowercased to match the key built per transaction below
                        key: `${bankName.toLowerCase()}|****${lastFour}|credit`
                    });
                }
            });
            
            setAvailableBankAccounts(accounts);
            // Select all accounts by default
            setSelectedBankAccounts(accounts.map(acc => acc.key));
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
        }
    }, []);

    const fetchExpenseTransactions = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/expense`,
                {
                    credentials: "include"
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            console.log("credit expense: ", data);
            const sortedData = data.sort(
                (a, b) => new Date(b["Date (UTC)"]) - new Date(a["Date (UTC)"])
            );
            setExpenseTransactions(sortedData);
        } catch (error) {
            console.error(
                "Error fetching credit card expense transactions:",
                error
            );
        }
    }, []);

    const fetchDepositTransactions = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposits`,
                {
                    credentials: "include"
                }
            );
            if (!response.ok)
                throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();
            setDepositTransactions(data);
        } catch (error) {
            console.error(
                "Error fetching credit card deposit transactions:",
                error
            );
        }
    }, []);

    const fetchRipplingPayrollTransactions = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/rippling`,
                {
                    credentials: "include"
                }
            );
            if (!response.ok)
                throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();
            setRipplingPayrollTransactions(data);
        } catch (error) {
            console.error(
                "Error fetching credit card Rippling payroll transactions:",
                error
            );
        }
    }, []);

    const fetchAllTransactions = useCallback(async () => {
        await Promise.all([
            fetchExpenseTransactions(),
            fetchDepositTransactions(),
            fetchRipplingPayrollTransactions(),
        ]);
    }, [
        fetchExpenseTransactions,
        fetchDepositTransactions,
        fetchRipplingPayrollTransactions,
    ]);

    const fetchGrantList = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`,
                {
                    credentials: "include"
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const grantData = await response.json();
            setGrantList(grantData);
        } catch (error) {
            console.error("Error fetching grant list:", error);
        }
    }, []);

    const fetchCustomizedCategories = useCallback(async () => {
        try {
            const [flatResponse, groupedResponse] = await Promise.all([
                fetch(
                    `${process.env.REACT_APP_BACKEND_API_URL}/cc-expense-categories`,
                    { credentials: "include" }
                ),
                fetch(
                    `${process.env.REACT_APP_BACKEND_API_URL}/cc-expense-categories/by-expense-type`,
                    { credentials: "include" }
                )
            ]);
            if (flatResponse.ok) {
                const categories = await flatResponse.json();
                setCustomizedCategories(categories);
            }
            if (groupedResponse.ok) {
                const grouped = await groupedResponse.json();
                setCategoriesByExpenseType(grouped);
            }
        } catch (error) {
            console.error("Error fetching customized categories:", error);
        }
    }, []);

    const fetchExpenseTypes = useCallback(async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/cc-expense-categories/expense-types`,
                { credentials: "include" }
            );
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const types = await response.json();
            setExpenseTypes(types);
        } catch (error) {
            console.error("Error fetching expense types:", error);
        }
    }, []);

    useEffect(() => {
        fetchAllTransactions();
        fetchGrantList();
        fetchCustomizedCategories();
        fetchExpenseTypes();
        fetchBankAccounts();
    }, [fetchAllTransactions, fetchGrantList, fetchCustomizedCategories, fetchExpenseTypes, fetchBankAccounts]);

    // useEffect(() => {
    //   filterTransactions();
    // }, [expenseTransactions, showConfirmedRecords, expensePeriod, customStartDate, customEndDate]);

    useEffect(() => {
        filterTransactions();
    }, [
        expenseTransactions,
        showConfirmedRecords,
        expensePeriod,
        customStartDate,
        customEndDate,
        selectedBankAccounts,
    ]);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("bankType", selectedBank);

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/import`,
                    {
                        method: "POST",
                        credentials: "include",
                        body: formData,
                    }
                );

                if (response.ok) {
                    const result = await response.json();
                    alert(
                        `Import successful:\n` +
                            `Expense transactions: ${result.statistics.expense}\n` +
                            `Rippling transactions: ${result.statistics.rippling}\n` +
                            `Deposit transactions: ${result.statistics.deposit}`
                    );
                    await fetchAllTransactions();
                } else {
                    throw new Error("Failed to import transactions");
                }
            } catch (error) {
                console.error("Error importing transactions:", error);
                alert(`Error importing transactions: ${error.message}`);
            }
            event.target.value = "";
        }
    };

    const handleExpensePeriodChange = (event) => {
        const selectedPeriod = event.target.value;
        setExpensePeriod(selectedPeriod);
        if (selectedPeriod === "custom") {
            setShowCustomPeriodPopup(true);
        } else {
            setCustomStartDate("");
            setCustomEndDate("");
        }
    };

    const handleCustomPeriodSubmit = () => {
        setShowCustomPeriodPopup(false);
        setExpensePeriod("custom");
    };

    const filterTransactions = () => {
        let filtered = expenseTransactions;

        filtered = filtered.filter((t) => t.Confirmed !== "Yes");

        const currentDate = new Date();
        let startDate, endDate;

        switch (expensePeriod) {
            case "lastYear":
                startDate = new Date(currentDate.getFullYear() - 1, 0, 1);
                endDate = new Date(currentDate.getFullYear() - 1, 11, 31);
                break;
            case "lastQuarter":
                startDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() - 3,
                    1
                );
                endDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    0
                );
                break;
            case "lastMonth":
                startDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() - 1,
                    1
                );
                endDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth(),
                    0
                );
                break;
            case "custom":
                startDate = new Date(customStartDate);
                endDate = new Date(customEndDate);
                // adding one day to end date to temp fix time zone issues?
                endDate.setDate(endDate.getDate() + 1);
                endDate.setHours(23, 59, 59, 999);
                break;
            default:
                if (!isNaN(parseInt(expensePeriod))) {
                    startDate = new Date(parseInt(expensePeriod), 0, 1);
                    endDate = new Date(parseInt(expensePeriod), 11, 31);
                }
        }

        if (startDate && endDate) {
            filtered = filtered.filter((t) => {
                const transactionDate = new Date(t["Date (UTC)"]);
                return (
                    transactionDate >= startDate && transactionDate <= endDate
                );
            });
        }

        // Bank account filtering - normalize bank type to lowercase for comparison
        if (selectedBankAccounts.length > 0) {
            filtered = filtered.filter((t) => {
                const bank = (t["Bank Type"] || "Unknown").toLowerCase();
                const accountNumber = t["Last Four Digits"] ? `****${t["Last Four Digits"]}` : "N/A";
                const accountType = "credit";
                const accountKey = `${bank}|${accountNumber}|${accountType}`;
                return selectedBankAccounts.includes(accountKey);
            });
        }

        setFilteredTransactions(filtered);
    };

    const getExpensePeriodOptions = () => {
        const years = [
            ...new Set(
                expenseTransactions.map((t) =>
                    new Date(t["Date (UTC)"]).getFullYear()
                )
            ),
        ];
        return [
            { value: "all", label: "All" },
            ...years.map((year) => ({
                value: year.toString(),
                label: year.toString(),
            })),
            { value: "lastYear", label: "Last Year" },
            { value: "lastQuarter", label: "Last Quarter" },
            { value: "lastMonth", label: "Last Month" },
            { value: "custom", label: "Custom Period" },
        ];
    };

    const handleEditDefaultPaymentClick = (transaction) => {
        let defaultPayment = transaction["Default Payment"];
        if (typeof defaultPayment === "string") {
            try {
                defaultPayment = JSON.parse(defaultPayment);
            } catch (error) {
                defaultPayment = {};
            }
        }
        setSelectedTransaction({
            ...transaction,
            defaultPayment: defaultPayment,
        });
        setShowEditDefaultPaymentPopup(true);
    };

    const handleEditSubmit = async (updatedTransaction) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/${updatedTransaction.ID}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify(updatedTransaction),
                }
            );
            if (response.ok) {
                // Auto-unapprove if the transaction was checked
                const originalTransaction = expenseTransactions.find(t => t.ID === updatedTransaction.ID);
                if (originalTransaction && originalTransaction.Checked === "Yes") {
                    try {
                        await fetch(
                            `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/check/${updatedTransaction.ID}`,
                            {
                                method: "PUT",
                                credentials: "include",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ Checked: false }),
                            }
                        );
                    } catch (uncheckError) {
                        console.error("Error auto-unapproving transaction:", uncheckError);
                    }
                }
                fetchExpenseTransactions();
            } else {
                alert("Failed to update credit card transaction.");
            }
        } catch (error) {
            console.error("Error updating credit card transaction:", error);
            alert("Error updating credit card transaction.");
        }
    };

    const handleEditDefaultPaymentSubmit = async (selectedTransaction) => {
        if (!selectedTransaction) {
            alert("No transaction selected");
            return;
        }

        const totalPercentage = Object.values(
            selectedTransaction.defaultPayment
        ).reduce((sum, percentage) => sum + (parseFloat(percentage) || 0), 0);

        if (Math.abs(totalPercentage - 100) > 0.01) {
            alert(
                `Total percentage must equal 100%. Current total: ${totalPercentage.toFixed(
                    2
                )}%`
            );
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/defaultPayment/${selectedTransaction.ID}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        defaultPayment: selectedTransaction.defaultPayment,
                    }),
                }
            );

            if (response.ok) {
                // Auto-unapprove if the transaction was checked
                const originalTransaction = expenseTransactions.find(t => t.ID === selectedTransaction.ID);
                if (originalTransaction && originalTransaction.Checked === "Yes") {
                    try {
                        await fetch(
                            `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/check/${selectedTransaction.ID}`,
                            {
                                method: "PUT",
                                credentials: "include",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ Checked: false }),
                            }
                        );
                    } catch (uncheckError) {
                        console.error("Error auto-unapproving transaction:", uncheckError);
                    }
                }
                fetchExpenseTransactions();
                setShowEditDefaultPaymentPopup(false);
            } else {
                const errorText = await response.text();
                alert(`Failed to update default payment: ${errorText}`);
            }
        } catch (error) {
            console.error("Error updating default payment:", error);
            alert(`Error updating default payment: ${error.message}`);
        }
    };

    // Bulk select-all and bulk set expense type for credit card unconfirmed transactions
    const [showBulkCategoryPopup, setShowBulkCategoryPopup] = useState(false);
    const [bulkCategoryValue, setBulkCategoryValue] = useState("");

    const handleSelectAllVisible = async (shouldCheck) => {
        const visibleIds = filteredTransactions
            .filter((t) => t.Confirmed !== "Yes" && t.ID)
            .map((t) => t.ID);
        if (visibleIds.length === 0) return;
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/bulk-update`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ids: visibleIds, Checked: shouldCheck }),
            });
            if (!resp.ok) throw new Error('Bulk update failed');
            fetchExpenseTransactions();
        } catch (error) {
            console.error("Error in select-all:", error);
            alert("Error updating check status for visible records.");
        }
    };

    const handleBulkSetCategory = async () => {
        if (!bulkCategoryValue) {
            alert("Please choose an expense type to apply.");
            return;
        }
        const targets = filteredTransactions.filter((t) => t.Confirmed !== "Yes");
        if (targets.length === 0) {
            alert("No unconfirmed transactions to update.");
            return;
        }
        try {
            const nonGrantFundName =
                grantList.find((g) => g.toLowerCase().includes("non-grant")) || "non-grant funds";
            const actualGrants = grantList.filter(
                (g) => g.toLowerCase() !== nonGrantFundName.toLowerCase()
            );
            let newDefaultPayment = null;
            if (bulkCategoryValue === "N/A (Non-grant Item)") {
                newDefaultPayment = {};
                grantList.forEach((g) => { newDefaultPayment[g] = "0"; });
                newDefaultPayment[nonGrantFundName] = "100";
            } else if (actualGrants.length >= 1) {
                newDefaultPayment = {};
                grantList.forEach((g) => {
                    newDefaultPayment[g] = (g === actualGrants[0]) ? "100" : "0";
                });
            }
            const isPaymentClearingCategory = ["Internal Bank Transfer", "Credit Card Payment"].includes(bulkCategoryValue);
            const ids = targets.map((t) => t.ID).filter(Boolean);
            const body = { ids, grantCategory: bulkCategoryValue };
            if (isPaymentClearingCategory) body.customizedCategory = "N/A";
            if (newDefaultPayment) body.defaultPayment = JSON.stringify(newDefaultPayment);
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/bulk-update`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!resp.ok) throw new Error('Bulk update failed');
            fetchExpenseTransactions();
            setShowBulkCategoryPopup(false);
            setBulkCategoryValue("");
        } catch (error) {
            console.error("Error in bulk set category:", error);
            alert("Error setting categories.");
        }
    };

    const handleCheckboxChange = async (id, checked) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/check/${id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({ Checked: checked }),
                }
            );
            if (response.ok) {
                fetchExpenseTransactions();
            } else {
                alert("Failed to update check status.");
            }
        } catch (error) {
            console.error("Error updating check status:", error);
            alert("Error updating check status.");
        }
    };

    // Update the handleGrantCategoryChange function
    const handleGrantCategoryChange = async (transaction, newCategory) => {
        let updatedTransaction = {
            ...transaction,
            "Grant Category": newCategory,
            "Customized Category": [
                "Internal Bank Transfer",
                "Credit Card Payment",
            ].includes(newCategory)
                ? "N/A"
                : transaction["Customized Category"],
        };

        // Filter out "non-grant funds" to check if only one actual grant exists
        const nonGrantFundName =
            grantList.find((grant) =>
                grant.toLowerCase().includes("non-grant")
            ) || "non-grant funds";

        const actualGrants = grantList.filter(
            (grant) => grant.toLowerCase() !== nonGrantFundName.toLowerCase()
        );

        // Create a default payment object
        const newDefaultPayment = {};

        if (newCategory === "N/A (Non-grant Item)") {
            // Set all grant funds to 0%
            grantList.forEach((grant) => {
                newDefaultPayment[grant] = "0";
            });

            // Set 'Non-Grant Funds' to 100%
            newDefaultPayment[nonGrantFundName] = "100";
        } else if (actualGrants.length >= 1) {
            // For any other category, default 100% to the first actual grant
            grantList.forEach((grant) => {
                if (grant === actualGrants[0]) {
                    newDefaultPayment[grant] = "100";
                } else {
                    newDefaultPayment[grant] = "0";
                }
            });
        }

        // Only update the default payment if we're handling one of our special cases
        if (
            newCategory === "N/A (Non-grant Item)" ||
            actualGrants.length >= 1
        ) {
            updatedTransaction = {
                ...updatedTransaction,
                "Default Payment": JSON.stringify(newDefaultPayment),
            };
        }

        await handleEditSubmit(updatedTransaction);
    };

    const handleConfirmCheckedRecords = async () => {
        setShowConfirmModal(false);

        const checkedTransactions = filteredTransactions.filter((t) => t.Checked === "Yes");
        const checkedIds = checkedTransactions.map((t) => t.ID);

        if (checkedIds.length === 0) {
            alert("No records selected to confirm.");
            return;
        }

        // Validate unconfirmed checked transactions
        const errors = [];
        for (const t of checkedTransactions.filter(t => t.Confirmed !== "Yes")) {
            if (["Internal Bank Transfer", "Credit Card Payment", "N/A (Non-grant Item)"].includes(t["Grant Category"])) continue;
            const lineItems = getLineItems(t);
            const desc = `${new Date(t["Date (UTC)"]).toLocaleDateString()} - $${Math.abs(parseFloat(t.Amount)).toFixed(2)}`;
            if (lineItems.length > 0) {
                const itemTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
                const transactionAmount = Math.abs(parseFloat(t.Amount) || 0);
                if (Math.abs(itemTotal - transactionAmount) > 0.01) {
                    errors.push(`${desc}: line item amounts ($${itemTotal.toFixed(2)}) ≠ transaction ($${transactionAmount.toFixed(2)})`);
                }
                for (let i = 0; i < lineItems.length; i++) {
                    if (!lineItems[i].grantCategory) {
                        errors.push(`${desc}: item "${lineItems[i].name || `#${i + 1}`}" has no category assigned`);
                    }
                    const itemPayment = lineItems[i].payment || {};
                    const itemPaymentTotal = grantList.reduce((sum, g) => sum + (parseFloat(itemPayment[g]) || 0), 0);
                    if (Math.abs(itemPaymentTotal - 100) > 0.01) {
                        errors.push(`${desc}: item "${lineItems[i].name || `#${i + 1}`}" grant split = ${itemPaymentTotal.toFixed(1)}% (must be 100%)`);
                    }
                }
            } else {
                const payment = JSON.parse(t["Default Payment"] || "{}");
                const total = grantList.reduce((sum, g) => sum + (parseFloat(payment[g]) || 0), 0);
                if (Math.abs(total - 100) > 0.01) {
                    errors.push(`${desc}: payment total = ${total.toFixed(1)}% (must be 100%)`);
                }
            }
        }
        if (errors.length > 0) {
            alert(`Cannot confirm. The following issues must be fixed:\n\n${errors.join('\n')}`);
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/expense/confirmChecked`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({ checkedIds }),
                }
            );

            if (response.ok) {
                await fetchExpenseTransactions();
                alert("Checked records confirmed successfully");
            } else {
                const errorText = await response.text();
                alert(`Failed to confirm checked records: ${errorText}`);
            }
        } catch (error) {
            console.error("Error confirming checked records:", error);
            alert("Error confirming checked records.");
        }
    };

    const handleAutoFill = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API_URL}/credit-card/expense/autofill`,
                {
                    method: "POST",
                    credentials: "include",
                }
            );

            if (response.ok) {
                await fetchExpenseTransactions();
                alert("Auto-fill completed successfully");
            } else {
                alert("Failed to auto-fill data.");
            }
        } catch (error) {
            console.error("Error auto-filling data:", error);
            alert("Error auto-filling data.");
        }
    };

    return (
        <div className={"credit-card-expense-container"}>
            {/* relative z-30 keeps the Bank Accounts drop-down above the table's
                sticky header instead of being clipped by it. */}
            <div className="card p-6 relative z-30 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Credit Card Transactions</h2>
                
                {/* Filters */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
                        <select
                            value={expensePeriod}
                            onChange={handleExpensePeriodChange}
                            className="input-field"
                        >
                            {getExpensePeriodOptions().map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <BankAccountFilter
                        availableAccounts={availableBankAccounts}
                        selectedAccounts={selectedBankAccounts}
                        onSelectionChange={setSelectedBankAccounts}
                        label="Bank Accounts"
                    />
                </div>

                <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                    ref={fileInputRef}
                />

                {/* Actions: bank details, categories and autofill on the left; submit on the right */}
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        onClick={() => setShowBankColumns(!showBankColumns)}
                        className="btn-secondary flex items-center gap-2"
                    >
                        <svg
                            className={`w-4 h-4 transition-transform duration-200 ${showBankColumns ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {showBankColumns ? 'Hide' : 'Show'} Bank Details
                    </button>

                    <Link to="/customized-categories">
                        <button className="btn-secondary">
                            Edit Expense Type/Subcategory
                        </button>
                    </Link>

                    <button
                        onClick={handleAutoFill}
                        className="btn-secondary"
                    >
                        AutoFill
                    </button>

                    <button
                        onClick={() => setShowConfirmModal(true)}
                        className="ml-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                        Submit Approved Records
                    </button>
                </div>
            </div>

            {/* Main Expense Table */}
            <div className="card overflow-hidden relative z-0">
                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Credit Card Expenses</h2>
                </div>
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date (UTC)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                {showBankColumns && (
                                    <>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account #</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    </>
                                )}
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Description</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <div className="flex flex-col items-start gap-1">
                                        <span>Expense Type</span>
                                        <button
                                            onClick={() => setShowBulkCategoryPopup(true)}
                                            className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 normal-case font-medium"
                                            title="Set expense type for all unconfirmed transactions"
                                        >
                                            Bulk Set
                                        </button>
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subcategory</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan={grantList.length || 1}>Payment (%)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 z-20 bg-gray-50" style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                                    <div className="flex flex-col items-center gap-1">
                                        <span>Approve</span>
                                        <button
                                            onClick={() => {
                                                const visibleUnconfirmed = filteredTransactions.filter(t => t.Confirmed !== "Yes");
                                                const allChecked = visibleUnconfirmed.length > 0 && visibleUnconfirmed.every(t => t.Checked === "Yes");
                                                handleSelectAllVisible(!allChecked);
                                            }}
                                            className="text-[10px] px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 normal-case font-medium"
                                            title="Toggle select all visible unconfirmed transactions"
                                        >
                                            Select All
                                        </button>
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th className="px-4 py-2"></th>
                                <th className="px-4 py-2"></th>
                                {showBankColumns && (
                                    <>
                                        <th className="px-4 py-2"></th>
                                        <th className="px-4 py-2"></th>
                                        <th className="px-4 py-2"></th>
                                    </>
                                )}
                                <th className="px-4 py-2"></th>
                                <th className="px-4 py-2"></th>
                                <th className="px-4 py-2"></th>
                                {grantList.map((grant) => (
                                    <th key={grant} className="px-2 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100 border-l border-gray-200">
                                        {grant}
                                    </th>
                                ))}
                                <th className="px-4 py-2"></th>
                                <th className="px-4 py-2 sticky right-0 z-20 bg-gray-50"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredTransactions.map((transaction, index) => (
                            <React.Fragment key={transaction.ID}>
                                <tr
                                    className={`hover:bg-gray-50 transition-colors duration-150 ${
                                        transaction.Checked === "Yes"
                                            ? "bg-primary-50 border-l-4 border-primary-400"
                                            : (index % 2 === 1 ? "bg-gray-50" : "")
                                    }`}
                                >
                                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                        {new Date(
                                            transaction["Date (UTC)"]
                                        ).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-red-600 whitespace-nowrap">
                                        $
                                        {Math.abs(
                                            parseFloat(transaction.Amount)
                                        ).toFixed(2)}
                                    </td>
                                    {showBankColumns && (
                                        <>
                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{transaction["Bank Type"] || "Unknown"}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{transaction["Last Four Digits"] ? `****${transaction["Last Four Digits"]}` : "N/A"}</td>
                                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">Credit</td>
                                        </>
                                    )}
                                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={transaction["Bank Description"]}>{transaction["Bank Description"]}</td>
                                    <td className="px-4 py-3">
                                        {getLineItems(transaction).length > 0 ? (
                                            <span className="text-xs text-gray-400">—</span>
                                        ) : (
                                        <select
                                            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                            value={transaction["Grant Category"]}
                                            onChange={(e) =>
                                                handleGrantCategoryChange(
                                                    transaction,
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">
                                                Select Category
                                            </option>
                                            {categories.map((category) => (
                                                <option
                                                    key={category}
                                                    value={category}
                                                >
                                                    {category}
                                                </option>
                                            ))}
                                        </select>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        {getLineItems(transaction).length > 0 ? (
                                            <span className="text-xs text-gray-400">—</span>
                                        ) : (
                                        <select
                                            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                                            value={
                                                transaction["Customized Category"]
                                            }
                                            onChange={(e) =>
                                                handleEditSubmit({
                                                    ...transaction,
                                                    "Customized Category":
                                                        e.target.value,
                                                })
                                            }
                                            disabled={[
                                                "Internal Bank Transfer",
                                                "Credit Card Payment",
                                            ].includes(
                                                transaction["Grant Category"]
                                            )}
                                        >
                                            <option value="N/A">N/A</option>
                                            {(() => {
                                                const selectedExpenseType = transaction["Grant Category"] || '';
                                                const typeSpecific = categoriesByExpenseType[selectedExpenseType] || [];
                                                const merged = [...new Set(typeSpecific)].filter(c => c !== 'N/A');
                                                return merged.map((category) => (
                                                    <option
                                                        key={category}
                                                        value={category}
                                                    >
                                                        {category}
                                                    </option>
                                                ));
                                            })()}
                                        </select>
                                        )}
                                    </td>
                                    {grantList.map((grant) => {
                                        const payment = JSON.parse(transaction["Default Payment"] || "{}");
                                        const percentage = payment[grant] || "0";
                                        const isItemized = getLineItems(transaction).length > 0;
                                        return (
                                            <td key={grant} className="px-2 py-3 text-center text-sm border-l border-gray-200">
                                                {isItemized ? <span className="text-gray-400">—</span> : `${percentage}%`}
                                            </td>
                                        );
                                    })}
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-1">
                                            <button
                                                className="text-xs px-2 py-1 bg-primary-50 text-primary-700 rounded hover:bg-primary-100 transition-colors"
                                                onClick={() =>
                                                    handleEditDefaultPaymentClick(
                                                        transaction
                                                    )
                                                }
                                            >
                                                Edit Payment
                                            </button>
                                            <button
                                                className={`text-xs px-2 py-1 rounded transition-colors ${transaction.Note ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                                                onClick={() =>
                                                    handleEditNoteClick(transaction)
                                                }
                                                title={
                                                    transaction.Note
                                                        ? "Edit note"
                                                        : "Add note"
                                                }
                                            >
                                                {transaction.Note
                                                    ? "Edit Note"
                                                    : "Add Note"}
                                            </button>
                                            <button
                                                className={`text-xs px-2 py-1 rounded transition-colors ${getLineItems(transaction).length > 0 ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                                                onClick={() => toggleItemization(transaction.ID)}
                                            >
                                                {getLineItems(transaction).length > 0 ? `Items (${getLineItems(transaction).length})` : 'Itemize'}
                                            </button>
                                        </div>
                                    </td>
                                    <td className={`px-4 py-3 text-center sticky right-0 z-10 ${transaction.Checked === "Yes" ? "bg-primary-50" : (index % 2 === 1 ? "bg-gray-50" : "bg-white")}`} style={{ boxShadow: '-2px 0 4px rgba(0,0,0,0.05)' }}>
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                            checked={transaction.Checked === "Yes"}
                                            onChange={(e) =>
                                                handleCheckboxChange(
                                                    transaction.ID,
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </td>
                                </tr>
                                {/* Line Items sub-rows */}
                                {expandedItemRows[transaction.ID] && (
                                    <>
                                        {getLineItems(transaction).map((item, itemIdx) => {
                                            const itemPayment = item.payment || {};
                                            const itemPaymentTotal = grantList.reduce((sum, g) => sum + (parseFloat(itemPayment[g]) || 0), 0);
                                            const colCount = 5 + (showBankColumns ? 3 : 0) + (grantList.length || 1) + 2;
                                            return (
                                            <tr key={item.id} className="bg-blue-50 border-b border-blue-100">
                                                <td className="pl-8 pr-2 py-1.5 text-xs text-gray-500">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-gray-400">#{itemIdx + 1}</span>
                                                        <input type="text" className="w-24 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                            defaultValue={item.name || ''} placeholder="Name"
                                                            onBlur={(e) => updateLineItem(transaction, item.id, 'name', e.target.value)} />
                                                    </div>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <input type="number" step="0.01" className="w-20 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                        defaultValue={item.amount} placeholder="Amount"
                                                        onBlur={(e) => updateLineItem(transaction, item.id, 'amount', e.target.value)} />
                                                </td>
                                                {showBankColumns && (<><td></td><td></td><td></td></>)}
                                                <td className="px-2 py-1.5">
                                                    <select className="w-24 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                        value={item.grantCategory || ''} onChange={(e) => updateLineItem(transaction, item.id, 'grantCategory', e.target.value)}>
                                                        <option value="">Select</option>
                                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <select className="w-24 text-xs border border-gray-300 rounded px-1 py-0.5"
                                                        value={item.customizedCategory || 'N/A'} onChange={(e) => updateLineItem(transaction, item.id, 'customizedCategory', e.target.value)}>
                                                        <option value="N/A">N/A</option>
                                                        {(() => {
                                                            const typeSpecific = categoriesByExpenseType[item.grantCategory || ''] || [];
                                                                        const merged = [...new Set(typeSpecific)].filter(c => c !== 'N/A');
                                                            return (merged.length > 0 ? merged : customizedCategories.filter(c => c !== 'N/A')).map(c => (
                                                                <option key={c} value={c}>{c}</option>
                                                            ));
                                                        })()}
                                                    </select>
                                                </td>
                                                {grantList.map(grant => (
                                                    <td key={grant} className="px-1 py-1.5 text-center border-l border-blue-100">
                                                        <PaymentPercentInput
                                                            inputKey={`${item.id}-${grant}-${itemPayment[grant] || "0"}`}
                                                            grant={grant}
                                                            grants={grantList}
                                                            payment={itemPayment}
                                                            onChange={(newPayment) => updateLineItem(transaction, item.id, 'payment', newPayment)}
                                                        />
                                                    </td>
                                                ))}
                                                <td className="px-2 py-1.5">
                                                    <div className="flex items-center gap-1">
                                                        {Math.abs(itemPaymentTotal - 100) > 0.01 && itemPaymentTotal > 0 && (
                                                            <span className="text-red-500 text-xs font-medium">{itemPaymentTotal.toFixed(0)}%</span>
                                                        )}
                                                        <button onClick={() => removeLineItem(transaction, item.id)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                                                    </div>
                                                </td>
                                                <td></td>
                                            </tr>
                                            );
                                        })}
                                        <tr className="bg-blue-50" style={{ borderBottom: '2px solid #93c5fd' }}>
                                            <td colSpan={20} className="pl-8 pr-4 py-2">
                                                <button onClick={() => addLineItem(transaction)}
                                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    Add Line Item
                                                </button>
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Additional Transaction Tables */}
            <div className={"additional-tables"}>
                <div className={"section"}>
                    <h2>Credit Card Deposit Transactions</h2>
                    <CreditCardDepositTable
                        depositTransactions={depositTransactions}
                        initialFilterStartDate={customStartDate}
                        initialFilterEndDate={customEndDate}
                    />
                </div>
            </div>

            {/* Note Editing Popup */}
            {showEditNotePopup && transactionToEditNote && (
                <div className="popup-overlay">
                    <div className="popup">
                        <h3>Edit Transaction Note</h3>
                        <div className="popup-content">
                            <div className="transaction-details">
                                <p>
                                    <strong>Date:</strong>{" "}
                                    {new Date(
                                        transactionToEditNote["Date (UTC)"]
                                    ).toLocaleDateString()}
                                </p>
                                <p>
                                    <strong>Description:</strong>{" "}
                                    {transactionToEditNote["Bank Description"]}
                                </p>
                                <p>
                                    <strong>Amount:</strong> $
                                    {Math.abs(
                                        parseFloat(transactionToEditNote.Amount)
                                    ).toFixed(2)}
                                </p>
                            </div>
                            <div className="note-textarea-container">
                                <label>Note:</label>
                                <textarea
                                    value={transactionToEditNote.Note || ""}
                                    onChange={(e) =>
                                        setTransactionToEditNote({
                                            ...transactionToEditNote,
                                            Note: e.target.value,
                                        })
                                    }
                                    placeholder="Add your notes here..."
                                    rows={4}
                                />
                            </div>
                            <div className="popup-buttons">
                                <button onClick={handleSaveNote}>
                                    Save Note
                                </button>
                                <button
                                    onClick={() => setShowEditNotePopup(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Period Popup */}
            {showCustomPeriodPopup && (
                <div className={"popup-overlay"}>
                    <div className={"popup"}>
                        <h3>Select Custom Period</h3>
                        <div className={"popup-content"}>
                            <div className={"date-input-group"}>
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) =>
                                        setCustomStartDate(e.target.value)
                                    }
                                />
                            </div>
                            <div className={"date-input-group"}>
                                <label>End Date</label>
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) =>
                                        setCustomEndDate(e.target.value)
                                    }
                                />
                            </div>
                            <div className={"popup-buttons"}>
                                <button onClick={handleCustomPeriodSubmit}>
                                    Apply
                                </button>
                                <button
                                    onClick={() =>
                                        setShowCustomPeriodPopup(false)
                                    }
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Payment Popup */}
            {showEditDefaultPaymentPopup && selectedTransaction && (
                <div className={"popup-overlay"}>
                    <div className={"popup"}>
                        <h3>Edit Payment</h3>
                        <div className={"popup-content"}>
                            {grantList.map((grant) => (
                                <div
                                    key={grant}
                                    className={"grant-input-group"}
                                >
                                    <label>{grant}</label>
                                    <input
                                        type="number"
                                        value={
                                            selectedTransaction.defaultPayment[
                                                grant
                                            ] || ""
                                        }
                                        onChange={(e) => {
                                            const newDefaultPayment = {
                                                ...selectedTransaction.defaultPayment,
                                                [grant]: e.target.value,
                                            };
                                            setSelectedTransaction({
                                                ...selectedTransaction,
                                                defaultPayment:
                                                    newDefaultPayment,
                                            });
                                        }}
                                        min="0"
                                        max="100"
                                        step="0.01"
                                    />
                                </div>
                            ))}
                            <div className={"popup-buttons"}>
                                <button
                                    onClick={() =>
                                        handleEditDefaultPaymentSubmit(
                                            selectedTransaction
                                        )
                                    }
                                >
                                    Save
                                </button>
                                <button
                                    onClick={() =>
                                        setShowEditDefaultPaymentPopup(false)
                                    }
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmSubmitModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmCheckedRecords}
            />

            {showBulkCategoryPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bulk Set Expense Type</h3>
                        <p className="text-sm text-gray-600 mb-3">
                            Apply this expense type to all unconfirmed transactions visible in the current view:
                        </p>
                        <select
                            className="input-field mb-4 w-full border border-gray-300 rounded px-2 py-1"
                            value={bulkCategoryValue}
                            onChange={(e) => setBulkCategoryValue(e.target.value)}
                        >
                            <option value="">Select expense type</option>
                            {categories.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                        <div className="flex justify-end gap-2">
                            <button
                                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                                onClick={() => { setShowBulkCategoryPopup(false); setBulkCategoryValue(""); }}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                onClick={handleBulkSetCategory}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    //   return (
    //     <div className={'credit-card-expense-container'}>
    //       <div className={'expense-controls'}>
    //         {/* Bank Type Selection */}
    //         <div className={'control-group'}>
    //           <label>Bank Type:</label>
    //           <select
    //             value={selectedBank}
    //             onChange={(e) => setSelectedBank(e.target.value)}
    //             className={'bank-select'}
    //           >
    //             {bankOptions.map(option => (
    //               <option key={option.value} value={option.value}>
    //                 {option.label}
    //               </option>
    //             ))}
    //           </select>
    //         </div>

    //         {/* Period Selection */}
    //         <div className={'control-group'}>
    //           <label>Period:</label>
    //           <select value={expensePeriod} onChange={handleExpensePeriodChange}>
    //             {getExpensePeriodOptions().map(option => (
    //               <option key={option.value} value={option.value}>{option.label}</option>
    //             ))}
    //           </select>
    //         </div>

    //         {/* Action Buttons */}
    //         {/* <div className={'button-group'}>
    //           <button
    //             className={'control-button'}
    //             onClick={() => setShowConfirmedRecords(!showConfirmedRecords)}
    //           >
    //             {showConfirmedRecords ? 'Hide Confirmed Records' : 'Show Confirmed Records'}
    //           </button>

    //           <input
    //             type="file"
    //             accept=".csv"
    //             onChange={handleFileUpload}
    //             style={{ display: 'none' }}
    //             ref={fileInputRef}
    //           />

    //           <button
    //             className={'control-button'}
    //             onClick={() => fileInputRef.current.click()}
    //           >
    //             Import Credit Card Transactions
    //           </button>

    //           <Link to="/customized-categories">
    //             <button className={'control-button'}>
    //               Edit Customized Categories
    //             </button>
    //           </Link>
    //         </div>
    //       </div>
    //    */}

    //    {/* Action Buttons */}
    //    <div className={'button-group'}>
    //   <button
    //     className={'control-button'}
    //     onClick={() => setShowConfirmedRecords(!showConfirmedRecords)}
    //   >
    //     {showConfirmedRecords ? 'Hide Confirmed Records' : 'Show Confirmed Records'}
    //   </button>

    //   <button
    //     className={'control-button'}
    //     onClick={handleConfirmCheckedRecords}
    //   >
    //     Confirm Checked Records
    //   </button>

    //   <button
    //     className={'control-button'}
    //     onClick={handleAutoFill}
    //   >
    //     AutoFill
    //   </button>

    //   <input
    //     type="file"
    //     accept=".csv"
    //     onChange={handleFileUpload}
    //     style={{ display: 'none' }}
    //     ref={fileInputRef}
    //   />

    //   <button
    //     className={'control-button'}
    //     onClick={() => fileInputRef.current.click()}
    //   >
    //     Import Credit Card Transactions
    //   </button>

    //   <Link to="/customized-categories">
    //     <button className={'control-button'}>
    //       Edit Customized Categories
    //     </button>
    //   </Link>
    // </div>
    // </div>

    //       {/* Main Expense Table */}
    //       <div className={'table-container'}>
    //         <h2>Credit Card Expenses</h2>
    //         <table className={'expense-table'}>
    //           <thead>
    //             <tr>
    //               <th>Check</th>
    //               <th>Date (UTC)</th>
    //               <th>Amount</th>
    //               <th>Bank Description</th>
    //               <th>Grant Category</th>
    //               <th>Customized Category</th>
    //               <th>Default Payment (%)</th>
    //               <th>Actions</th>
    //             </tr>
    //           </thead>
    //           <tbody>
    //             {filteredTransactions.map((transaction) => (
    //               <tr
    //                 key={transaction.ID}
    //                 className={transaction.Checked === 'Yes' ? 'checked-row' : ''}
    //               >
    //                 <td>
    //                   <input
    //                     type="checkbox"
    //                     checked={transaction.Checked === 'Yes'}
    //                     onChange={(e) => handleCheckboxChange(transaction.ID, e.target.checked)}
    //                   />
    //                 </td>
    //                 <td>{new Date(transaction['Date (UTC)']).toLocaleDateString()}</td>
    //                 <td className={'amount'}>
    //                   ${Math.abs(parseFloat(transaction.Amount)).toFixed(2)}
    //                 </td>
    //                 <td>{transaction['Bank Description']}</td>
    //                 <td>
    //                   <select
    //                     value={transaction['Grant Category']}
    //                     onChange={(e) => handleGrantCategoryChange(transaction, e.target.value)}
    //                   >
    //                     <option value="">Select Category</option>
    //                     {categories.map((category) => (
    //                       <option key={category} value={category}>
    //                         {category}
    //                       </option>
    //                     ))}
    //                   </select>
    //                 </td>
    //                 <td>
    //                   <select
    //                     value={transaction['Customized Category']}
    //                     onChange={(e) => handleEditSubmit({
    //                       ...transaction,
    //                       'Customized Category': e.target.value
    //                     })}
    //                     disabled={['Company Account Transfer', 'Credit Card Payment']
    //                       .includes(transaction['Grant Category'])}
    //                   >
    //                     <option value="N/A">N/A</option>
    //                     {customizedCategories.map((category) => (
    //                       <option key={category} value={category}>
    //                         {category}
    //                       </option>
    //                     ))}
    //                   </select>
    //                 </td>
    //                 <td>
    //                   {Object.entries(JSON.parse(transaction['Default Payment'] || '{}')).map(([source, percentage]) =>
    //                     `${source}: ${percentage}%`
    //                   ).join(', ')}
    //                 </td>
    //                 <td>
    //                   <button
    //                     className={'edit-button'}
    //                     onClick={() => handleEditDefaultPaymentClick(transaction)}
    //                   >
    //                     Edit Default Payment
    //                   </button>
    //                 </td>
    //               </tr>
    //             ))}
    //           </tbody>
    //         </table>
    //       </div>

    //       {/* Additional Transaction Tables */}
    //       <div className={'additional-tables'}>
    //         <div className={'section'}>
    //           <h2>Credit Card Rippling Payroll Transactions</h2>
    //           <table className={'transaction-table'}>
    //             <thead>
    //               <tr>
    //                 <th>Date (UTC)</th>
    //                 <th>Description</th>
    //                 <th>Amount</th>
    //               </tr>
    //             </thead>
    //             <tbody>
    //               {ripplingPayrollTransactions.map((transaction) => (
    //                 <tr key={transaction.ID}>
    //                   <td>{new Date(transaction['Date (UTC)']).toLocaleDateString()}</td>
    //                   <td>{transaction['Bank Description']}</td>
    //                   <td className={'amount'}>
    //                     ${Math.abs(parseFloat(transaction.Amount)).toFixed(2)}
    //                   </td>
    //                 </tr>
    //               ))}
    //             </tbody>
    //           </table>
    //         </div>

    //         {/* <div className={'section'}>
    //           <h2>Credit Card Deposit Transactions</h2>
    //           <table className={'transaction-table'}>
    //             <thead>
    //               <tr>
    //                 <th>Date (UTC)</th>
    //                 <th>Description</th>
    //                 <th>Amount</th>
    //               </tr>
    //             </thead>
    //             <tbody>
    //               {depositTransactions.map((transaction) => (
    //                 <tr key={transaction.ID}>
    //                   <td>{new Date(transaction['Date (UTC)']).toLocaleDateString()}</td>
    //                   <td>{transaction['Bank Description']}</td>
    //                   <td className={'amount'}>
    //                     ${parseFloat(transaction.Amount).toFixed(2)}
    //                   </td>
    //                 </tr>
    //               ))}
    //             </tbody>
    //           </table>
    //         </div> */}

    //       <div className={'section'}>
    //         <h2>Credit Card Deposit Transactions</h2>
    //         <CreditCardDepositTable
    //           depositTransactions={depositTransactions}
    //         />
    //       </div>

    //       </div>

    //       {/* Custom Period Popup */}
    //       {showCustomPeriodPopup && (
    //         <div className={'popup-overlay'}>
    //           <div className={'popup'}>
    //             <h3>Select Custom Period</h3>
    //             <div className={'popup-content'}>
    //               <div className={'date-input-group'}>
    //                 <label>Start Date</label>
    //                 <input
    //                   type="date"
    //                   value={customStartDate}
    //                   onChange={(e) => setCustomStartDate(e.target.value)}
    //                 />
    //               </div>
    //               <div className={'date-input-group'}>
    //                 <label>End Date</label>
    //                 <input
    //                   type="date"
    //                   value={customEndDate}
    //                   onChange={(e) => setCustomEndDate(e.target.value)}
    //                 />
    //               </div>
    //               <div className={'popup-buttons'}>
    //                 <button onClick={handleCustomPeriodSubmit}>Apply</button>
    //                 <button onClick={() => setShowCustomPeriodPopup(false)}>Cancel</button>
    //               </div>
    //             </div>
    //           </div>
    //         </div>
    //       )}

    //       {/* Edit Default Payment Popup */}
    //       {showEditDefaultPaymentPopup && selectedTransaction && (
    //         <div className={'popup-overlay'}>
    //           <div className={'popup'}>
    //             <h3>Edit Default Payment</h3>
    //             <div className={'popup-content'}>
    //               {grantList.map(grant => (
    //                 <div key={grant} className={'grant-input-group'}>
    //                   <label>{grant}</label>
    //                   <input
    //                     type="number"
    //                     value={selectedTransaction.defaultPayment[grant] || ''}
    //                     onChange={(e) => {
    //                       const newDefaultPayment = {
    //                         ...selectedTransaction.defaultPayment,
    //                         [grant]: e.target.value
    //                       };
    //                       setSelectedTransaction({
    //                         ...selectedTransaction,
    //                         defaultPayment: newDefaultPayment
    //                       });
    //                     }}
    //                     min="0"
    //                     max="100"
    //                     step="0.01"
    //                   />
    //                 </div>
    //               ))}
    //               <div className={'popup-buttons'}>
    //                 <button onClick={() => handleEditDefaultPaymentSubmit(selectedTransaction)}>
    //                   Save
    //                 </button>
    //                 <button onClick={() => setShowEditDefaultPaymentPopup(false)}>
    //                   Cancel
    //                 </button>
    //               </div>
    //             </div>
    //           </div>
    //         </div>
    //       )}
    //     </div>
    //   );
};

export default CreditCardExpense;
